"use client";

import { InsightsCardsConsumption } from "../insights-cards-consumption/insights-cards-consumption";
import {
  AlertTriangle,
  CheckCircle,
  Info,
  OctagonAlert,
  Ticket,
} from "lucide-react";
import { useActionsTrack } from "@/_old/features/analytics";
import { useNavigate } from "react-router-dom";
import { useExcessConsumption } from "./excess-consumption.hooks";
import { useTranslation } from "react-i18next";
import { InsightsCardsConsumptionSkeleton } from "../insights-cards-consumption/insights-cards-consumption-skeleton";
import { useMemo, useCallback } from "react";
import { truncateToTwoDecimals } from "@/_old/utils/number";
import { useChatbot } from "@/core/components/floating-chatbot/context/chatbot-context";
import dayjs from "dayjs";

function replaceVars(
  template: string,
  vars: Record<string, string | number>,
): string {
  return Object.entries(vars).reduce(
    (acc, [key, value]) =>
      acc.replace(new RegExp(`\\{${key}\\}`, "g"), String(value)),
    template,
  );
}

export function ExcessConsumption({
  startDate,
  endDate,
  companyId,
  carousel = false,
}: {
  startDate: string;
  endDate: string;
  companyId: number;
  carousel?: boolean;
}) {
  const { onActionClick } = useActionsTrack();
  const { openWithMessage } = useChatbot();
  const navigate = useNavigate();

  const [translate] = useTranslation(["dashboard-charts"]);
  const [translateDashboard] = useTranslation(["dashboard"]);

  const { data, isLoading } = useExcessConsumption({
    startDate,
    endDate,
    companyId,
  });

  const tag = useMemo((): {
    type: "danger" | "default" | "good" | "warning";
    tag: string;
    subtitle: string;
    description: string;
    content: string;
    icon: React.ReactNode;
  } => {
    const percentage: number = data?.percentage ?? 0;

    if (percentage === 0) {
      return {
        type: "default",
        tag: translate("dashboard-package-excess-start-of-cycle"),
        subtitle: translate("dashboard-package-excess-subtitle", {
          excess: truncateToTwoDecimals(0),
        }),
        description: translate("dashboard-package-excess-started-description"),
        content: translate("dashboard-package-excess-started-content", {
          package: data?.package,
        }),
        icon: <Info size={18} />,
      };
    }

    if (percentage > 100) {
      return {
        type: "danger",
        tag: translate("dashboard-package-excessive-consumption"),
        subtitle: translate("dashboard-package-excess-subtitle-surplus", {
          excess: truncateToTwoDecimals(data?.surplus ?? 0),
        }),
        description: translate("dashboard-package-excess-surplus-description"),
        content: translate("dashboard-package-excess-surplus-content", {
          package: data?.package ?? "",
        }),
        icon: <OctagonAlert size={18} />,
      };
    }

    if (percentage >= 80) {
      return {
        type: "warning",
        tag: translate("dashboard-package-excess-high-consumption"),
        subtitle: translate("dashboard-package-excess-subtitle", {
          excess: truncateToTwoDecimals(data?.surplus ?? 0),
        }),
        description: translate("dashboard-package-excess-high-description"),
        content: translate("dashboard-package-excess-high-content", {
          package: data?.package ?? "",
        }),
        icon: <AlertTriangle size={18} />,
      };
    }

    if (percentage >= 10) {
      return {
        type: "good",
        tag: translate("dashboard-package-excess-healthy-consumption"),
        subtitle: translate("dashboard-package-excess-subtitle", {
          excess: truncateToTwoDecimals(data?.surplus ?? 0),
        }),
        description: translate("dashboard-package-excess-good-description"),
        content: translate("dashboard-package-excess-good-content", {
          package: data?.package ?? "",
        }),
        icon: <CheckCircle size={18} />,
      };
    }

    return {
      type: "default",
      tag: translate("dashboard-package-excess-start-of-cycle"),
      subtitle: translate("dashboard-package-excess-subtitle", {
        excess: truncateToTwoDecimals(data?.surplus ?? 0),
      }),
      description: translate("dashboard-package-excess-started-description"),
      content: translate("dashboard-package-excess-started-content", {
        package: data?.package ?? "",
      }),
      icon: <Info size={18} />,
    };
  }, [data]);

  const handleButtonClick = useCallback(() => {
    onActionClick("CLICK_INSIGHT_PACKAGE_PAGE", "DASHBOARD");

    if (!data || data.percentage == null) {
      navigate("/packages");
      return;
    }

    const percentage = data.percentage ?? 0;
    const cicloFim = dayjs(endDate).format("DD/MM/YYYY");
    const vars = {
      pacote: data.package ?? "",
      ciclo_fim: cicloFim,
    };

    let fullTemplateKey = "dashboard-excess-lia-full-new-cycle";
    let shortTemplateKey = "dashboard-excess-lia-short-new-cycle";

    if (percentage > 100) {
      fullTemplateKey = "dashboard-excess-lia-full-surplus";
      shortTemplateKey = "dashboard-excess-lia-short-surplus";
    } else if (percentage >= 80) {
      fullTemplateKey = "dashboard-excess-lia-full-risk";
      shortTemplateKey = "dashboard-excess-lia-short-risk";
    } else if (percentage >= 10) {
      fullTemplateKey = "dashboard-excess-lia-full-healthy";
      shortTemplateKey = "dashboard-excess-lia-short-healthy";
    }

    const fullTemplate = (translateDashboard(fullTemplateKey) as string) ?? "";
    const shortTemplate =
      (translateDashboard(shortTemplateKey) as string) ?? "";

    const fullMessage = replaceVars(fullTemplate, vars);
    const shortMessage = replaceVars(shortTemplate, vars);
    openWithMessage(fullMessage, shortMessage);
  }, [
    data,
    endDate,
    onActionClick,
    navigate,
    openWithMessage,
    translateDashboard,
  ]);

  if (isLoading || !data) {
    return <InsightsCardsConsumptionSkeleton carousel={carousel} />;
  }

  return (
    <InsightsCardsConsumption
      tag={tag.tag}
      description={tag.description}
      subtitle={tag.subtitle}
      buttonText={translate("dashboard-package-excess-button-text")}
      icon={carousel ? tag.icon : <Ticket size={18} />}
      content={
        <div
          className="text-[#020617]"
          dangerouslySetInnerHTML={{
            __html: tag.content,
          }}
        />
      }
      type={tag.type}
      onClick={handleButtonClick}
      carousel={carousel}
    />
  );
}
